// The Deals page's own API shapes (module 10 U151, U152).
//
// A module of this track's own rather than lines in apps/portal/src/api.ts, which belongs to the
// records-page track (infra/agent-guard/OWNERS). The reply flow does the same with reply/api.ts.
import { viewFetch as fetch } from '../view-as.js';
import { Unauthenticated } from '../api.js';

export class DealsError extends Error {
  constructor(readonly status: number, message: string) { super(message); }
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res
      .json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new DealsError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

/** One step of the paper track, exactly as the store defines it (U153). The page never names them. */
export interface StageStep { code: string; name: string; sortOrder: number; lifecycle: string }

export interface Moved {
  kind: 'stage' | 'value' | 'heard';
  detail: string;
  source: string | null;
  when: string;
}

export interface Heard {
  id: string;
  side: string | null;
  organisationId: string | null;
  organisationName: string | null;
  headline: string;
  quote: string | null;
  heardIn: string | null;
  heardAt: string;
  ownerPrincipalId: string | null;
  ownerName: string | null;
  mine: boolean;
}

export interface Offer {
  id: string;
  operatorLabel: string;
  organisationId: string | null;
  state: string;
  stateJudged: boolean;
  holderName: string | null;
  mine: boolean;
  integrationRoute: string | null;
  feeAmount: string | null;
  feeCurrency: string | null;
  feeText: string | null;
  contactedOn: string | null;
  note: string | null;
  lastHeardAt: string | null;
  source: string;
  /** The operator's next meeting at a show that has not ended (crm.event_meeting); null when none is booked. */
  atShow: { event: string; at: string | null; on: string | null } | null;
}

/** The next step open on a deal. Null means nothing is open, and the card says that in words. */
export interface NextStep {
  title: string;
  dueAt: string | null;
  ownerPrincipalId: string | null;
  ownerName: string | null;
  mine: boolean;
}

export interface DeskDeal {
  id: string;
  matterId: string;
  title: string;
  organisationId: string;
  /** BUGS.md D97: null once the company is put away, and the page names none. */
  organisationName: string | null;
  onIt: string[];
  side: string | null;
  stageCode: string;
  stageName: string;
  stageInferred: boolean;
  lifecycle: string;
  pausedUntil: string | null;
  stateReason: string | null;
  holderPrincipalId: string | null;
  holderName: string | null;
  mine: boolean;
  moved: Moved | null;
  lastHeardAt: string | null;
  nextFixedAt: string | null;
  silentDays: number | null;
  /** Whether the silence is worth saying. Decided by the API, never by this page. */
  silent: boolean;
  nextStep: NextStep | null;
  fromHubspot: boolean;
  /** Cobrain's lead and one or two options, from the reader's own newest triage pass (D63). */
  lead: Lead | null;
  /** A rights deal's roll-out folded in (U154). Null when no operator has a row. */
  rollout: Rollout | null;
  /** Cobrain closed it on a completion notice, told to the holder alone for a fortnight (U147). */
  closed: Closed | null;
  /** Signed copies the store already holds for this OPEN deal, as the reader may see them (U185). */
  signedCopies: HeldCopy[];
}

export interface HeldCopy { noticeSignalId: string; document: string; signedOn: string }

export interface Closed {
  judgementId: string;
  closedAt: string;
  fromStage: string;
  document: string | null;
  contractId: string | null;
  noticeAt: string | null;
}

export interface LeadOption { key: string; kind: string; label: string; note: string | null; params: Record<string, unknown> | null }

export interface Lead {
  passItemId: string;
  reason: string | null;
  rankNo: number;
  tier: number;
  options: LeadOption[];
  /** The message that raised the item (module 10 U416; brief section A), read as the person may read it; null when none. */
  source: { signalId: string; channel: 'email' | 'teams' | 'whatsapp' } | null;
}

export interface Rollout {
  total: number;
  offered: number;
  signed: number;
  rows: Array<{ id: string; operatorLabel: string; state: string; stateJudged: boolean; holderName: string | null; lastHeardAt: string | null }>;
}

/** One deal, for the head of its own page. The route 404s rather than returning this empty. */
export interface DealHead {
  id: string;
  title: string;
  organisationId: string;
  /** BUGS.md D97: null once the company is put away, and the page names none. */
  organisationName: string | null;
  /** The stored logo of the company the deal is filed on (pipeline RC7, RC17); null or absent, and the Logo draws its initials. */
  organisationLogo?: string | null;
  side: string | null;
  stageCode: string;
  stageName: string;
  lifecycle: string;
  pausedUntil: string | null;
  stateReason: string | null;
  /** BUGS.md X8: put aside to be sorted by hand, or archived; absent from an older server, and read as neither. */
  putAside?: 'to_sort' | 'archived' | null;
  holderPrincipalId: string | null;
  holderName: string | null;
  mine: boolean;
  /** BUGS.md D73: their contact, as Add deal's "Their contact" saved it, when the reader may read that person. */
  contact?: { id: string; name: string } | null;
  /** BUGS.md D82: a rights deal's terms as Add deal saved them; null on a sale. */
  rightsTerms?: RightsTerms | null;
  /** BUGS.md D129: the right a rights deal is on, opening its page; null on a sale. */
  property?: { id: string; name: string } | null;
}
export interface RightsTerms {
  video: boolean | null; data: boolean | null; exclusive: string | null; territory: string | null; from: string | null; until: string | null;
}

export interface Desk {
  store: { tenantId: string; name: string };
  me: string;
  /** True when the read hit its ceiling, so a band count is a floor and says so. */
  more: boolean;
  stages: StageStep[];
  deals: DeskDeal[];
  heard: Heard[];
  /** The deal-proposals pass writes for somebody in this store, so an empty band means it heard nothing. */
  heardOn: boolean;
  /** The store's active people, for "Give it to someone else". */
  team: Array<{ principalId: string; name: string }>;
  /** Who a deal nobody holds can be given to: the whole team, joined or not (U156, REQ-2026-09-24-40). */
  giveTo: Array<{ principalId: string; name: string }>;
}

/** The desk's find: deals, companies and people by name or alias, as the reader. */
export interface Found {
  /** `archived`: listed after the live ones and marked Archived (BUGS.md D199, U391). */
  deals: Array<{ id: string; title: string; organisationName: string | null; archived?: boolean }>;
  companies: Array<{ id: string; name: string }>;
  people: Array<{ id: string; name: string; organisationName: string | null }>;
}

/** What a take, a give or a remove on a deal nobody holds did (U156): the action's id is what Undo names. */
export interface Allocated { dealId: string; actionId: string; kind: 'take' | 'give' | 'remove' | 'hand'; to: string | null }

// ---------------------------------------------------------------------------------------------------------------------
// Accounts by heat (GET /api/deals/heat; module 10 U211 to U226). The shapes are apps/api/src/deals/heat.ts and
// heat-shape.ts, written out here because the portal does not import the API's code.

export interface Named { principalId: string; name: string | null; mine: boolean }
export interface Words { text: string; from: 'sheet' | 'signal'; kind: string | null; at: string | null }
/** term: the Padel Change Log's status on a figure the team's account sheet set (pipeline I8, U429); only Confirmed is agreed. */
export interface HeatFigure { amount: string; currency: string; setBy: string; by: Named | null; source: string | null; at: string; term?: { status: string; agreed: boolean; comment: string | null; buyout: number | null } | null }
export interface HeldBy { holdId: string; title: string; blocks: 'next_step' | 'go_live'; owner: Named | null }
/** answerApproved: whether one of the four approvers approved the usual answer; until then the card marks it (U245). */
export interface ObjectionMet { code: string | null; name: string; teamAnswer: string | null; answerSource: string | null; answerApproved: boolean; said: Words | null }
/** answerApproved: whether the usual answer the wording carries is approved; null with no objection or no answer (U245). */
export interface Rewrite { id: string; original: Words; wording: string; objection: string | null; answerApproved: boolean | null; state: 'kept' | 'put_back' }

/** The label over a usual answer on the card: marked until an approver approves it (U245, the drawn Deals-card state). */
export const usualAnswerLabel = (o: Pick<ObjectionMet, 'teamAnswer' | 'answerApproved'>): string =>
  o.teamAnswer !== null && !o.answerApproved ? 'Our usual answer, not approved yet' : 'Our usual answer';
/** The label over Cobrain's wording of a note: marked while the answer it carries is not approved (U245). */
export const wordingLabel = (w: Pick<Rewrite, 'answerApproved'>): string =>
  w.answerApproved === false ? 'Cobrain’s wording, its answer not approved yet' : 'Cobrain’s wording';
export interface ClientPromise { id: string; dealId: string; statement: string; dueAt: string | null; precision: string; waiting: Named | null; since: string }
export interface HeatStep { kind: 'task' | 'commitment'; id: string; title: string; dueAt: string | null; owner: Named | null }
export interface FromSheet {
  termsHeard: string | null; nextStep: string | null; nextStepOwner: string | null; due: string | null;
  whoMovesNext: string | null; routeToLive: string | null; metBy: string | null;
}
export type HeatStatus = 'parked' | 'held' | 'waiting' | 'step' | 'no_step';

// What is live on an opportunity (apps/api/src/deals/live.ts; pipeline P5, the brief DEALS-LIVE-CARD-BRIEF-2026-10-04.md).
export type Opens = { page: 'item' | 'meeting' | 'chat'; id: string };
export interface DealMessage {
  signalId: string; kind: string; direction: string; subject: string | null; from: string | null; fromMe: boolean;
  to: string | null; attachment: string | null; at: string; today: boolean; opens: Opens | null;
}
export interface CobrainMove {
  call: { meetingId: string; at: string; paragraph: string } | null;
  fromCall: boolean; callAt: string | null;
  changeId: string; field: 'stage' | 'keenness'; toCode: string | null; toName: string | null; fromCode: string | null; fromName: string | null;
  at: string; undoUntil: string | null;
  from: DealMessage | null; undone: { by: Named | null; at: string } | null;
}
export interface KeennessBy { by: Named | null; why: string | null; cobrain: { keenness: number; at: string | null; fromCall: boolean } | null }
/** One entry of the running record (P9, U297): what one side said in one message, or a person's own words. */
export interface SaidLine {
  side: 'them' | 'us'; channel: 'mail' | 'teams' | 'call' | 'meeting' | 'notes'; at: string;
  who: string | null; mine: boolean; words: string; signalId: string | null; opens: Opens | null;
}
/** A message "In their words" lists, with what each side said in it once Cobrain has read it. */
export interface HeardMessage { message: DealMessage; said: SaidLine[] }
/** Cobrain's last move of "We expect" (U294), with the sum it worked out and the words it read. */
export interface ValueMove {
  changeId: string; fromAmount: string | null; fromCurrency: string | null; toAmount: string | null; toCurrency: string | null;
  sum: Record<string, unknown> | null; words: string | null; who: string | null; at: string; undoUntil: string | null;
  message: DealMessage | null; undone: { by: Named | null; at: string } | null;
}
export interface EarlierValue {
  amount: string | null; currency: string | null; setBy: string | null; by: Named | null; at: string;
  message: DealMessage | null; undone: boolean; undo: boolean;
}
/** How an opportunity pays (U295; migration 208). Amounts as written, decimal strings. */
export interface PayTerms {
  basis: 'fixed' | 'share' | 'per_match' | 'unknown'; currency: string | null;
  feeAmount: string | null; feePeriod: 'year' | 'month' | null;
  sharePct: string | null; shareOf: 'ggr' | 'ngr' | 'turnover' | 'net_revenue' | null;
  minimumAmount: string | null; minimumPeriod: 'year' | 'month' | null;
  matchRate: string | null; matchCount: number | null; setBy: 'person' | 'sheet' | null;
}
export interface DealValue {
  pay: PayTerms | null;
  base: { amount: string; period: 'year' | 'month'; setBy: 'person' | 'cobrain'; by: Named | null; who: string | null; at: string | null; message: DealMessage | null } | null;
  move: ValueMove | null;
  earlier: EarlierValue[];
  statements: Array<{ at: string; amount: string; currency: string; covers: string | null; base: string | null }>;
  question: { at: string; direction: 'higher' | 'lower'; message: DealMessage | null; mine: boolean } | null;
}
/** What our team decided about a deal, read by Cobrain from a meeting, call, chat or mail of our team only (B8, U328). */
export interface TeamDecision {
  id: string; kind: 'hold' | 'priority' | 'terms' | 'send' | 'step'; words: string; by: string | null; owner: Named | null;
  at: string; from: DealMessage | null; quote: string | null; undoUntil: string;
}
export interface LiveOpportunity {
  stageCode: string | null; stageName: string | null; stageInferred: boolean; stageRead: boolean; lastMove: CobrainMove | null;
  latest: DealMessage[]; messages: number; todayIds: string[]; keennessBy: KeennessBy | null;
  /** Pipeline P9 (REQ-2026-10-05-14): each move on its own, what was said, their newest words, and the Value block. */
  keennessMove?: CobrainMove | null; stageMove?: CobrainMove | null;
  heard?: HeardMessage[]; theirLast?: SaidLine | null; value?: DealValue;
  /** Pipeline B8 (REQ-2026-10-05-30): what our team decided about it, newest first. */
  decisions?: TeamDecision[];
}
/** How it pays, as the card's Change sends it (POST /api/deals/:id/pay). */
export interface PayInput {
  basis: PayTerms['basis']; currency: string | null;
  fee: { amount: number; period: 'year' | 'month' } | null;
  share: { pct: number; of: NonNullable<PayTerms['shareOf']> } | null;
  minimum: { amount: number; period: 'year' | 'month' } | null;
  match: { rate: number; count: number | null } | null;
  base: { amount: number; period: 'year' | 'month' } | null;
}
export interface NewToday {
  what: 'moved' | 'keenness' | 'message' | 'messages'; dealId: string; at: string; toCode: string | null; toName: string | null;
  message: DealMessage | null; count: number;
}
export interface HeatRow {
  id: string; matterId: string; title: string; product: string | null; family: string; dealType: string | null; side: string | null;
  sourceRef: string | null; eventId: string | null; lifecycle: string; pausedUntil: string | null;
  /** Why a parked deal is parked, as the person who parked it wrote it; null on an open one (pipeline FT1). */
  stateReason: string | null;
  /** BUGS.md D97: a company's or an account's name is null once it is put away, and the page names none. */
  organisationId: string; organisationName: string | null; accountId: string; accountName: string | null; accountOwner: Named | null;
  /** The stored logo of the company the deal is filed on (pipeline RC7, RC17); null or absent, and the Logo draws its initials. */
  organisationLogo?: string | null;
  suppliesUs: boolean; holder: Named | null;
  /** BUGS.md D73: their contact, as Add deal's "Their contact" saved it, when the reader may read that person. Absent
   *  from an older API. */
  contact?: { id: string; name: string } | null;
  keenness: number | null; keennessSetBy: string | null; keennessAt: string | null;
  clientWords: Words | null; territories: Array<{ country: string; keenness: number }>; holds: HeldBy[]; objections: ObjectionMet[];
  rewrites: Rewrite[]; promises: ClientPromise[]; step: HeatStep | null; figure: HeatFigure | null;
  lastHeardAt: string | null; firstContactAt: string | null; fromSheet: FromSheet | null; createdAt: string; status: HeatStatus;
  /** Null when the live read did not see it (made or closed between the two reads). */
  live: LiveOpportunity | null;
  /** Its own news today and its own silence, by the account line's rules: the company record's line (U240). */
  newToday: NewToday | null;
  silentDays: number | null;
}
export interface HeatMoney { home: string; withFigure: number; of: number; other: Array<{ currency: string; amount: string; count: number }> }
export interface ByKeenness { k5: string; k4: string; k3: string; k2: string; k1: string; none: string }
export interface HeatRing extends HeatMoney { byKeenness: ByKeenness }
export interface HoldLine {
  holdId: string; title: string; owner: Named | null; source: string; openedAt: string; since: string;
  holds: number; keen: number; beforeGoLive: number; ring: HeatRing; proposed: HeatMoney | null; dealIds: string[];
}
/** A decision recorded on Waiting on us, with what Undo names (routes/deal-holds.ts). */
export interface HoldDecided { actionId: string; holdId: string; title: string; words: string; undoUntil: string }
export interface PromiseLine extends ClientPromise { accountId: string; accountName: string | null; product: string | null; keenness: number | null; daysPast: number | null }
export interface ShowLine { id: string; name: string; startsOn: string; endsOn: string; opportunities: number; accounts: number; meetingsWithoutNotes: number; reworded: number }
export interface Heat {
  store: { tenantId: string; name: string };
  me: string;
  heardOn: boolean;
  leads: Record<string, Lead>;
  heard: Heard[];
  team: Array<{ principalId: string; name: string }>;
  /** Who the card's "Give the deal to someone" offers: the whole team, joined or not (U156, U262). */
  giveTo: Array<{ principalId: string; name: string }>;
  now: string;
  homeCurrency: string | null;
  shows: ShowLine[];
  rings: Array<HeatRing & { family: string; dealIds: string[] }>;
  figures: HeatMoney & { yoursWithout: number };
  waitingOnUs: HoldLine[];
  waitingOnThem: PromiseLine[];
  proposals: { yours: number; colleagues: number; anyone: number; since: string | null };
  /** The list (U278, U279): every open or parked opportunity once, ranked by the API (heat-shape.ts lineOrder): keenness
   *  5 to 1, then none; ties to the stage further along, the larger year's figure, the latest the client said or did. A
   *  parked one is a line like any other, its status saying till when and why (pipeline FT1; no fold of its own). */
  lines: HeatRow[];
  setAside: { archived: number; toSort: number; toSortSince: string | null };
  addYourFigures: Array<{ dealId: string; accountName: string | null; product: string | null; title: string; keenness: number | null }>;
  /** The store's stages, by its own names (U153, U237): the card's "Correct the stage". */
  stages: StageStep[];
  /** The reader may change a deal here (Commercial or an admin, U232); the card's edits are drawn only then (U152). */
  mayEdit: boolean;
  dayStart: string | null;
  /** What Deals opens on for the reader (U283): their own Everyone or Mine, kept on the server. */
  dealsList: DealsList;
}

/** Everyone or Mine at the front of the filter bar (U283). */
export type DealsList = 'everyone' | 'mine';

/** What one card write did: the change it made, which is what its Undo names. */
export interface CardWritten { dealId: string; changeId: string }

/** One change on a deal, as the history lists it (apps/api/src/deals/live.ts Change). */
export interface HistoryChange {
  changeId: string;
  /** stage_id, keenness, revenue_year, product, value_amount, paused_until, ... */
  field: string;
  /** 'inferred' (Cobrain), 'user_override' (a person) or 'seed' (an import). */
  source: string;
  from: { code: string | null; name: string | null };
  to: { code: string | null; name: string | null };
  oldState: Record<string, unknown> | null;
  newState: Record<string, unknown> | null;
  why: string | null;
  by: Named | null;
  at: string;
  undoUntil: string | null;
  /** It took back an earlier change. */
  undoes: string | null;
  /** A later change took this one back. */
  undoneBy: string | null;
  message: DealMessage | null;
}
export type HistoryEvent = { kind: 'message'; at: string; message: DealMessage } | { kind: 'change'; at: string; change: HistoryChange };
/**
 * What a person did to the deal itself (apps/api/src/deals/live.ts DealAct; BUGS.md D53): its making by Add deal, a hand,
 * give, take or remove, and a bring-in of another record's history or a thread. Undone by undoAct, by the person who did
 * it, inside its week.
 */
export interface DealAct {
  actionId: string;
  kind: 'add' | 'hand' | 'give' | 'take' | 'remove' | 'join' | 'keep' | 'archive' | 'won' | 'lost';
  by: Named | null;
  /** Whom it was handed or given to; the taker for a take; null otherwise. */
  to: Named | null;
  at: string;
  /** Until when it can be undone; null when it never could, or the week has passed. */
  undoUntil: string | null;
  undoneAt: string | null;
  /** A bring-in's other record, by its title. */
  title: string | null;
}
/** GET /api/deals/:id/history: everything filed under a deal and every change made to it, newest first. */
export interface DealHistory {
  /** The stored logo of the company the deal is filed on (pipeline RC7, RC17); null or absent, and the Logo draws its initials. */
  /** BUGS.md D97: organisationName is null once the company is put away, and the page names none. */
  deal: { id: string; matterId: string; title: string; product: string | null; organisationId: string; organisationName: string | null; organisationLogo?: string | null };
  events: HistoryEvent[];
  /** The acts on the deal itself, newest first; absent from an API older than D53's. */
  acts?: DealAct[];
  messages: number;
  changes: number;
  /** More than the read's ceiling of either: the newest are listed. */
  more: boolean;
}
/** One deal put aside to be sorted by hand (U222), as HubSpot left it. */
export interface ToSort {
  id: string; title: string; organisationId: string; organisationName: string | null; stageName: string; lifecycle: string;
  valueAmount: string | null; valueCurrency: string | null; expectedCloseOn: string | null; hubspotDealId: string | null;
  since: string; reason: string; by: Named | null; holder: Named | null;
}

export const dealsApi = {
  heat: (store: string) =>
    fetch(`/api/deals/heat?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json<Heat>(r)),
  toSort: (store: string) =>
    fetch(`/api/deals/to-sort?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json<{ deals: ToSort[] }>(r)),
  find: (store: string, q: string) =>
    fetch(`/api/deals/find?store=${encodeURIComponent(store)}&q=${encodeURIComponent(q)}`, { credentials: 'same-origin' }).then((r) => json<Found & { q: string }>(r)),
  desk: (store: string) =>
    fetch(`/api/deals/desk?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json<Desk>(r)),
  history: (store: string, dealId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/history?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' })
      .then((r) => json<DealHistory>(r)),
  offers: (store: string, dealId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/offers?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' })
      .then((r) => json<{ dealId: string; deal: DealHead; offers: Offer[] }>(r)),
  park: (store: string, dealId: string, until: string, reason: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/park`, post({ store, until, reason })).then((r) => json<{ pausedUntil: string }>(r)),
  unpark: (store: string, dealId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/unpark`, post({ store })).then((r) => json<{ lifecycle: string }>(r)),
  /** A decision Waiting on us holds, recorded in the reader's words: its line leaves (REQ-2026-10-07-171). */
  decideHold: (store: string, holdId: string, words: string) =>
    fetch(`/api/deals/holds/${encodeURIComponent(holdId)}/decide`, post({ store, words }))
      .then((r) => json<HoldDecided>(r)),
  undoDecideHold: (store: string, holdId: string, actionId: string) =>
    fetch(`/api/deals/holds/${encodeURIComponent(holdId)}/decide/undo`, post({ store, actionId })).then((r) => json<{ undone: true }>(r)),
  accept: (store: string, proposalId: string, organisationId: string | null) =>
    fetch(`/api/deals/proposals/${encodeURIComponent(proposalId)}/accept`, post({ store, organisationId }))
      .then((r) => json<{ dealId: string; matterId: string }>(r)),
  closeOnCopy: (store: string, dealId: string, noticeSignalId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/signed-copy/close`, post({ store, noticeSignalId })).then((r) => json<{ contractId: string }>(r)),
  notThisDeal: (store: string, dealId: string, noticeSignalId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/signed-copy/not-this-deal`, post({ store, noticeSignalId })).then((r) => json<{ ok: true }>(r)),
  reopen: (store: string, dealId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/reopen`, post({ store })).then((r) => json<{ stage: string }>(r)),
  pass: (store: string, proposalId: string, to: string) =>
    fetch(`/api/deals/proposals/${encodeURIComponent(proposalId)}/pass`, post({ store, to })).then((r) => json<{ from: string | null; to: string }>(r)),
  notADeal: (store: string, proposalId: string, reason: string) =>
    fetch(`/api/deals/proposals/${encodeURIComponent(proposalId)}/not-a-deal`, post({ store, reason })).then((r) => json<{ state: string }>(r)),
  take: (store: string, dealId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/take`, post({ store })).then((r) => json<Allocated>(r)),
  give: (store: string, dealId: string, to: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/give`, post({ store, to })).then((r) => json<Allocated>(r)),
  remove: (store: string, dealId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/remove`, post({ store })).then((r) => json<Allocated>(r)),
  /** A deal somebody holds, handed to a colleague (U262); undone by undoAllocation. */
  hand: (store: string, dealId: string, to: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/hand`, post({ store, to })).then((r) => json<Allocated>(r)),
  setKeenness: (store: string, dealId: string, keenness: number, why: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/keenness`, post({ store, keenness, why })).then((r) => json<CardWritten>(r)),
  setFigure: (store: string, dealId: string, amount: number | null, currency: string | null) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/figure`, post({ store, amount, currency })).then((r) => json<CardWritten>(r)),
  setPay: (store: string, dealId: string, pay: PayInput) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/pay`, post({ store, ...pay })).then((r) => json<CardWritten>(r)),
  setProduct: (store: string, dealId: string, product: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/product`, post({ store, product })).then((r) => json<CardWritten>(r)),
  setStage: (store: string, dealId: string, stageCode: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/stage`, post({ store, stageCode })).then((r) => json<CardWritten>(r)),
  undoChange: (store: string, changeId: string) =>
    fetch(`/api/deals/changes/${encodeURIComponent(changeId)}/undo`, post({ store })).then((r) => json<{ changeId: string; dealId: string; undone: true }>(r)),
  /** B8: a decision of the team Cobrain read, taken back (D62). */
  undoDecision: (store: string, decisionId: string) =>
    fetch(`/api/deals/decisions/${encodeURIComponent(decisionId)}/undo`, post({ store })).then((r) => json<{ decisionId: string; dealId: string; undone: true }>(r)),
  /** The reader's own press on Everyone or Mine, kept for them on the server (U283). */
  setDealsList: (store: string, view: DealsList) =>
    fetch('/api/deals/list-view', post({ store, view })).then((r) => json<{ view: DealsList }>(r)),
  /** BUGS.md D142, D146: an act the history lists, taken back (POST /api/deals/:id/acts/:actionId/undo). */
  undoAct: (store: string, dealId: string, actionId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/acts/${encodeURIComponent(actionId)}/undo`, post({ store })).then((r) => json<{ dealId: string; undone: true }>(r)),
  undoAllocation: (store: string, dealId: string, actionId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/allocation/undo`, post({ store, actionId })).then((r) => json<{ undone: Allocated['kind'] }>(r)),
  /** BUGS.md D54: a deal put aside, kept (back on Deals) or archived by hand; undone by undoSort inside its week. */
  sort: (store: string, dealId: string, how: 'keep' | 'archive') =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/sort`, post({ store, how })).then((r) => json<SortedByHand>(r)),
  undoSort: (store: string, dealId: string, actionId: string) =>
    fetch(`/api/deals/${encodeURIComponent(dealId)}/sort/undo`, post({ store, actionId })).then((r) => json<{ undone: true }>(r)),
};

/** A deal put aside, sorted by hand (POST /api/deals/:id/sort, deals/sort.ts): its action, for Undo, and until when. */
export interface SortedByHand { dealId: string; actionId: string; how: 'keep' | 'archive'; undoUntil: string }

function post(body: unknown): RequestInit {
  return { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
}
